import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal, Platform, RefreshControl, ScrollView, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions,
} from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../../context/AuthContext';
import { ThemeColors, useTheme } from '../../context/ThemeContext';
import { canteenAPI, serverMessage } from '../../services/walletApi';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Toast } from '../common/Toast';
import { Logger } from '../../utils/logger';
import {
  CopyResult, ExtrasSelection, PlanSelection, areExtrasOpen, choiceCount, copyLastWeek, cutOffText, dayHasActivity, dayOrderName, isPlannerToday, dayLabel,
  isoDate, mondayOf, parseIsoDate, sameDayCutOffNote, changeDifference, declaredAllergies,
  extraKey, extraMax, extraOrderedQty, extrasCount, extrasCutOffText, extrasOrderText, hasOrderedMeals, isAllergyReason,
  isOptionChoosable, isSlotChoosable, menuWeekLabel, orderInfoLine, placedSummary, placedTotals, orderReview, onlyDay, servedForLabel, placeBlock, previousWeek, pruneExtras, pruneSelection,
  sameLunchEveryDay, selectionTotal, shortDayName, skippedText, slotKey, slotRows, stepExtra, toPlanRequest, toggleChoice,
  visibleDays, walletShortfall, weekChoices, weekRangeLabel,
} from '../../utils/canteenPlanner';
import type {
  CanteenPlanResult, CanteenPlanner, CanteenPlannerDay, CanteenPlannerExtrasOrder, CanteenPlannerOption, CanteenPlannerSlot,
} from '../../types/wallet';
import { BusyOverlay, Notice, Segmented } from '../wallet/WalletBits';
import { CanteenHistory } from './CanteenHistory';
import { CanteenMenu } from './CanteenMenu';
import { ActionButton, Chips, HeroHeader, StatusPill, money } from '@shared/commerce';

/**
 * The canteen week planner for one child (owner, 22 Oct 2026 design, "4 · Parent › Week planner") -
 * the parent app and the parent web portal alike.
 *
 * Three views (owner, 2 Nov 2026): Order meals, Menu (the week's menu to read, CanteenMenu) and Order
 * history. Order meals works by day (one day's meals and extras in their own order - today or any day
 * ahead) or for the whole week.
 *
 * Always for the child chosen at the top of the app (owner, 2 Nov 2026: the screen had its own child
 * chips too, so it was unclear who an order was for). Pick the week (this week, next week, the week
 * after); choose at most one dish per
 * meal slot per day (lunch option 1 OR 2); the wallet bar compares the week's total with the child's
 * wallet; Review weekly order shows what will be charged, and Confirm order sends every choice at once. Under each day's meals an Extras block
 * ("3 · Extras": a la carte drinks, beverages, snacks) takes a quantity per item (up to 5 a day,
 * already ordered included) until the day's extras cut-off; extras count in the week's total and
 * can be placed on their own. Wide screens show the whole week as a grid (meals down, days across,
 * an Extras row last); a phone shows one day at a time. The server prices every choice and
 * enforces every rule; its message is shown as it is.
 */

type Styles = ReturnType<typeof createStyles>;
type ToastState = { message: string; type: 'success' | 'info' | 'error' } | null;
/** An order the parent asked to cancel - a meal or a day's extras - and what the confirm says about it. */
type CancelTarget = { canteenOrderId: string; description: string; amount: number };
/** An ordered meal the parent wants to swap for another dish of the same meal. */
type ChangeTarget = { day: CanteenPlannerDay; slot: CanteenPlannerSlot; ordered: NonNullable<CanteenPlannerSlot['ordered']> };
/** What Same lunch / Copy last week did, shown under the buttons (a toast sits below the fold on the web). */
type ToolNote = { tone: 'info' | 'warn'; text: string };
/** A Same lunch / Copy last week that would change meals the parent already chose - applied once confirmed. */
type PendingCopy = { result: CopyResult; note: ToolNote };

/** How many weeks back Copy last week looks for one with meals ordered. */
const COPY_LOOKBACK_WEEKS = 4;

/** The grid needs about this much room; below it the planner shows one day at a time. */
const GRID_MIN_WIDTH = 1100;

export function CanteenContent() {
  const { students, selectedStudent } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const wide = width >= GRID_MIN_WIDTH;

  const studentId = selectedStudent?.id ?? null;
  const childName = (selectedStudent?.name ?? '').split(/\s+/)[0] || 'your child';

  const weeks = useMemo(() => weekChoices(new Date()), []);
  const [from, setFrom] = useState(weeks[1].value);
  const [planner, setPlanner] = useState<CanteenPlanner | null>(null);
  const [selection, setSelection] = useState<PlanSelection>({});
  const [extras, setExtras] = useState<ExtrasSelection>({});
  const [dayIndex, setDayIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);
  // Order meals, the Menu to read, or Order history - one screen, no menu of its own.
  const [view, setView] = useState<'plan' | 'menu' | 'history'>('plan');
  const [historyKey, setHistoryKey] = useState(0);
  const [cancelling, setCancelling] = useState<CancelTarget | null>(null);
  // Change a dish (owner, 2 Nov 2026: from tomorrow onwards, never today's).
  const [changing, setChanging] = useState<ChangeTarget | null>(null);
  const [changeTo, setChangeTo] = useState<number | null>(null);
  const [toolNote, setToolNote] = useState<ToolNote | null>(null);
  const [pendingCopy, setPendingCopy] = useState<PendingCopy | null>(null);
  // What Place week just ordered: shown in an "Order placed" popup until the parent closes it.
  const [placedResult, setPlacedResult] = useState<CanteenPlanResult | null>(null);
  // The order review shown before anything is charged (QA, 31 Oct 2026).
  const [reviewing, setReviewing] = useState(false);
  // Order by day (owner, 2 Nov 2026): only the shown day's choices go into the order - today or any
  // day ahead. Plan the week orders every chosen day at once.
  const [mode, setMode] = useState<'week' | 'day'>('week');
  // A day to open once its week has loaded (Order for today, or Order this day from the Menu).
  const [wantDay, setWantDay] = useState<string | null>(null);
  const requestNo = useRef(0);

  const load = useCallback(async () => {
    if (!studentId) { setLoading(false); return; }
    const mine = ++requestNo.current;
    try {
      const data = await canteenAPI.getPlanner(studentId, from, 7);
      if (mine !== requestNo.current) { return; }
      setPlanner(data);
      setSelection(prev => pruneSelection(prev, data));
      setExtras(prev => pruneExtras(prev, data));
      setLoadError(null);
    } catch (err) {
      if (mine !== requestNo.current) { return; }
      Logger.error('Canteen', 'Failed to load the week planner', err);
      setPlanner(null);
      setLoadError(serverMessage(err, 'The week planner could not be loaded. Pull to refresh or try again later.'));
    } finally {
      if (mine === requestNo.current) { setLoading(false); }
    }
  }, [studentId, from]);

  // A different child or week: a fresh selection and that week's planner.
  useEffect(() => {
    setLoading(true); setSelection({}); setExtras({}); setActionError(null); setToolNote(null); setDayIndex(-1);
    load();
  }, [load]);

  const days = useMemo(() => visibleDays(planner), [planner]);
  const dayMode = mode === 'day';
  // On a phone (and when ordering by day), open on the first day something can still be chosen.
  useEffect(() => {
    if (dayIndex >= 0 || days.length === 0) { return; }
    const firstOpen = days.findIndex(d => d.slots.some(sl => isSlotChoosable(d, sl)) || areExtrasOpen(d));
    setDayIndex(firstOpen >= 0 ? firstOpen : 0);
  }, [days, dayIndex]);
  // ... or on the day asked for, once the week holding it has loaded (not the week shown before).
  useEffect(() => {
    if (!wantDay || loading || !planner) { return; }
    const all = planner.days;
    if (all.length === 0 || wantDay < all[0].date.slice(0, 10) || wantDay > all[all.length - 1].date.slice(0, 10)) { return; }
    const i = days.findIndex(d => d.date === wantDay);
    if (i >= 0) { setDayIndex(i); }
    setWantDay(null);
  }, [days, wantDay, loading, planner]);

  const refresh = async () => { setRefreshing(true); setHistoryKey(k => k + 1); await load(); setRefreshing(false); };

  const cur = planner?.currency ?? 'USD';
  // The day the phone shows (and the day ordered by day); Same lunch repeats the lunch chosen there first.
  const shownDay = days.length > 0 ? days[Math.max(0, Math.min(dayIndex, days.length - 1))] : null;
  const shownIsToday = !!shownDay && isPlannerToday(shownDay.date, planner?.today);
  // What the order holds: the whole week, or by day only the shown day's choices.
  const activeSelection = dayMode ? onlyDay(selection, shownDay?.date ?? '') : selection;
  const activeExtras = dayMode ? onlyDay(extras, shownDay?.date ?? '') : extras;
  const total = selectionTotal(activeSelection, planner, activeExtras);
  const count = choiceCount(activeSelection, planner);
  const extraCount = extrasCount(activeExtras, planner);
  const block = placeBlock(activeSelection, planner, activeExtras);
  const review = reviewing ? orderReview(activeSelection, planner, activeExtras) : null;
  const orderWord = dayMode && shownDay ? dayOrderName(shownDay.date, planner?.today) : 'weekly order';
  const { shortBy, left } = walletShortfall(total, planner?.walletBalance ?? 0);

  const choose = (day: CanteenPlannerDay, slot: CanteenPlannerSlot, option: CanteenPlannerOption) => {
    if (!isSlotChoosable(day, slot) || !isOptionChoosable(option)) { return; }
    setActionError(null);
    setToolNote(null);
    setSelection(prev => toggleChoice(prev, day.date, slot.slotId, option.optionNo));
  };

  const step = (day: CanteenPlannerDay, item: CanteenPlannerOption, delta: number) => {
    setActionError(null);
    setExtras(prev => stepExtra(prev, day, item, delta));
  };

  /** Applies a copy at once, or asks first when it would change meals the parent already chose. */
  const offerCopy = (result: CopyResult, note: ToolNote) => {
    if (result.replaced > 0) { setPendingCopy({ result, note }); return; }
    if (result.copied > 0) { setSelection(result.selection); }
    setToolNote(note);
  };

  const applyPendingCopy = () => {
    if (!pendingCopy) { return; }
    setSelection(pendingCopy.result.selection);
    setToolNote(pendingCopy.note);
    setPendingCopy(null);
  };

  /** "Copied 4 meals ... Not available: Wed lunch (past its cut-off)." */
  const copyNote = (result: CopyResult, done: string, none: string): ToolNote => {
    const skipped = result.skipped.length > 0 ? ` Not available: ${skippedText(result.skipped)}.` : '';
    return { tone: result.skipped.length > 0 || result.copied === 0 ? 'warn' : 'info', text: `${result.copied > 0 ? done : none}${skipped}` };
  };

  const sameLunch = () => {
    if (!planner) { return; }
    setActionError(null);
    const result = sameLunchEveryDay(selection, planner, wide ? undefined : shownDay?.date);
    if (!result.source) {
      setToolNote({ tone: 'info', text: 'Pick a lunch first, then tap Same lunch every day to have it on the other days too.' });
      return;
    }
    const meals = `${result.copied} more day${result.copied === 1 ? '' : 's'}`;
    offerCopy(result, copyNote(result,
      `${result.source.name} chosen for lunch on ${meals}.`,
      `No other day this week could take ${result.source.name}.`));
  };

  const copyLast = async () => {
    if (!studentId || !planner || busy) { return; }
    setBusy(true);
    setActionError(null);
    try {
      // The week just before is often not ordered yet (planning the week after): go back to the
      // latest week that has meals ordered.
      let week = previousWeek(from);
      let last: CanteenPlanner | null = null;
      for (let i = 0; i < COPY_LOOKBACK_WEEKS; i++) {
        const found = await canteenAPI.getPlanner(studentId, week, 7);
        if (hasOrderedMeals(found)) { last = found; break; }
        week = previousWeek(week);
      }
      if (!last) {
        setToolNote({ tone: 'warn', text: `No meals were ordered for ${childName} in the last ${COPY_LOOKBACK_WEEKS} weeks, so there is nothing to copy.` });
        return;
      }
      const result = copyLastWeek(selection, planner, last);
      const range = weekRangeLabel(week);
      offerCopy(result, copyNote(result,
        `Copied ${result.copied} meal${result.copied === 1 ? '' : 's'} from the week of ${range}.`,
        `Nothing from the week of ${range} could be copied to this week.`));
    } catch (err) {
      Logger.error('Canteen', 'Failed to copy last week', err);
      setActionError(serverMessage(err, 'Last week could not be read. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  /** Drops the choices that were in the order just placed or discarded; the rest of the week stays chosen. */
  const dropActive = () => {
    const without = <T extends Record<string, number>>(all: T, gone: Record<string, number>) =>
      Object.fromEntries(Object.entries(all).filter(([k]) => !(k in gone))) as T;
    setSelection(prev => without(prev, activeSelection));
    setExtras(prev => without(prev, activeExtras));
  };

  const discardOrder = () => {
    setReviewing(false);
    dropActive();
    setToolNote({ tone: 'info', text: `Nothing was ordered - the chosen meals were cleared.` });
  };

  /** Order one day, in its own order: that day's week, by day, on that day. */
  const orderDay = (date: string) => {
    setActionError(null);
    setToolNote(null);
    setView('plan');
    setMode('day');
    const monday = isoDate(mondayOf(parseIsoDate(date)));
    if (monday !== from) { setFrom(monday); }
    setWantDay(date);
  };
  /** Order for today: this week, by day, on today (the school's date once the planner has it). */
  const orderForToday = () => orderDay((planner?.today ?? '').slice(0, 10) || isoDate(new Date()));
  const chooseMode = (next: 'week' | 'day') => { setActionError(null); setToolNote(null); setMode(next); };
  const chooseWeek = (value: string) => { setFrom(value); };

  const placeWeek = async () => {
    if (!studentId || block || busy) { return; }
    setReviewing(false);
    setBusy(true);
    setActionError(null);
    try {
      const result = await canteenAPI.placePlan(studentId, toPlanRequest(activeSelection, planner, activeExtras));
      dropActive();
      setToolNote(null);
      setPlacedResult(result);
      await load();
    } catch (err) {
      Logger.error('Canteen', 'Failed to place the week', err);
      setActionError(serverMessage(err, 'The week could not be placed. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const cancelOrder = async () => {
    if (!studentId || !cancelling) { return; }
    const target = cancelling;
    setCancelling(null);
    setBusy(true);
    setActionError(null);
    try {
      const result = await canteenAPI.cancelOrder(studentId, target.canteenOrderId);
      setToast({ message: result.message, type: 'success' });
      await load();
    } catch (err) {
      Logger.error('Canteen', 'Failed to cancel the order', err);
      setActionError(serverMessage(err, 'The order could not be cancelled. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const startChange = (target: ChangeTarget) => { setActionError(null); setChangeTo(null); setChanging(target); };

  const changeOrder = async () => {
    if (!studentId || !changing || changeTo === null) { return; }
    const target = changing;
    setChanging(null);
    setBusy(true);
    setActionError(null);
    try {
      const result = await canteenAPI.changeOrder(studentId, target.ordered.canteenOrderId, changeTo);
      setToast({ message: result.message, type: 'success' });
      await load();
    } catch (err) {
      Logger.error('Canteen', 'Failed to change the order', err);
      setActionError(serverMessage(err, 'The dish could not be changed. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const goTopUp = () => navigation.navigate('Wallet', { tab: 'pay' });

  if (!studentId) { return <Notice>Choose a child at the top to order their canteen meals.</Notice>; }

  const allergyNames = (planner?.allergies ?? []).filter(a => a.flagType === 'ALLERGY').map(a => a.name.toLowerCase());
  const declared = declaredAllergies(planner);
  const slotProps = { styles, colors, selection, extras, currency: cur, onChoose: choose, onStep: step, onCancel: setCancelling, onChange: startChange };

  const header = (
    <HeroHeader
      title={view === 'history' ? `${childName}'s orders` : view === 'menu' ? `${childName}'s canteen menu` : `Order ${childName}'s meals`}
      subtitle={planner ? `${planner.priceLabel} · wallet ${money(planner.walletBalance, cur)}${planner.deliveryText ? ` · ${planner.deliveryText}` : ''}` : undefined}>
      {students.length > 1 && (
        <Text style={styles.heroNote} testID="canteen-child">
          {`For ${selectedStudent?.name ?? childName}, the child chosen at the top. To order for another child, choose them there.`}
        </Text>
      )}
      {view !== 'history' && <Chips onHero testID="canteen-week" options={weeks} value={from} onChange={chooseWeek} />}
    </HeroHeader>
  );

  const switcher = (
    <Segmented<'plan' | 'menu' | 'history'>
      options={[{ value: 'plan', label: 'Order meals' }, { value: 'menu', label: 'Menu' }, { value: 'history', label: 'Order history' }]}
      value={view}
      onChange={setView}
    />
  );

  const tools = (
    <View style={styles.stack}>
      <View testID="canteen-mode">
        <Segmented<'day' | 'week'>
          options={[{ value: 'day', label: 'Order by day' }, { value: 'week', label: 'Plan the week' }]}
          value={mode}
          onChange={chooseMode}
        />
      </View>
      <View style={styles.tools}>
        {!dayMode && (
          <>
            <ToolButton styles={styles} colors={colors} icon="repeat" label="Same lunch every day" onPress={sameLunch}
              disabled={!planner} testID="canteen-same-lunch" />
            <ToolButton styles={styles} colors={colors} icon="copy" label="Copy last week" onPress={copyLast}
              disabled={!planner} testID="canteen-copy-last-week" />
          </>
        )}
        <ToolButton styles={styles} colors={colors} icon="clock" label="Order for today" onPress={orderForToday}
          disabled={!planner || (dayMode && shownIsToday)} testID="canteen-order-today" />
      </View>
    </View>
  );

  const sameDay = sameDayCutOffNote(planner);
  const dayNote = dayMode && planner && (
    <View testID="canteen-day-only">
      <Notice tone="info">
        {!shownDay
          ? 'There is no canteen menu this week. Choose another week.'
          : `Ordering for ${shownIsToday ? `today only (${dayLabel(shownDay.date)})` : `${dayLabel(shownDay.date)} only`} - pick another day below. Meals past their cut-off can't be chosen.${sameDay ? ` ${sameDay}` : ''}`}
      </Notice>
    </View>
  );

  const toolResult = toolNote && (
    <View testID="canteen-tool-note">
      <Notice tone={toolNote.tone} onClose={() => setToolNote(null)}>{toolNote.text}</Notice>
    </View>
  );

  const walletBar = planner && (
    <View style={styles.walletBar} testID="canteen-wallet-bar">
      <View style={styles.walletInfo}>
        <Text style={styles.walletTitle}>
          {`${childName}'s ${!dayMode ? 'week' : shownIsToday ? 'order today' : `order for ${shownDay ? dayLabel(shownDay.date) : 'the day'}`}: ${money(total, cur)}`}
          <Text style={styles.muted}>
            {`  · wallet ${money(planner.walletBalance, cur)} · ${count} meal${count === 1 ? '' : 's'} · ${extraCount} extra${extraCount === 1 ? '' : 's'}`}
          </Text>
        </Text>
        <View style={styles.meter}>
          <View style={[styles.meterFill, {
            width: `${planner.walletBalance > 0 ? Math.min(100, (total / planner.walletBalance) * 100) : total > 0 ? 100 : 0}%`,
            backgroundColor: shortBy > 0 ? colors.danger : colors.primary,
          }]} />
        </View>
        <Text style={[styles.small, { color: shortBy > 0 ? colors.danger : colors.textSecondary }]}>
          {shortBy > 0
            ? `Top up ${money(shortBy, cur)} to place this ${dayMode ? 'order' : 'week'}.`
            : `${money(left, cur)} left after this ${dayMode ? 'order' : 'week'}.`}
        </Text>
      </View>
      <View style={styles.walletActions}>
        {block === 'OVER_BALANCE' && <ActionButton label="Top up" tone="gold" onPress={goTopUp} testID="canteen-top-up"
          style={styles.walletBtn} />}
        <ActionButton label={!dayMode ? 'Review weekly order' : shownIsToday ? "Review today's order" : 'Review order'} tone="green"
          onPress={() => setReviewing(true)} disabled={!!block || busy}
          testID="canteen-place-week" style={styles.walletBtn} />
      </View>
    </View>
  );

  const notices = (
    <>
      {loadError && <Notice tone="error">{loadError}</Notice>}
      {actionError && <Notice tone="error" onClose={() => setActionError(null)}>{actionError}</Notice>}
      {planner && !planner.isOrderingOpen && <Notice tone="warn">Canteen ordering is switched off at the moment.</Notice>}
      {planner && (!planner.walletsEnabled || !planner.walletId) && (
        <Notice tone="warn">Canteen meals are paid from the child's wallet, and there is no wallet to pay from yet. Ask the school office to open one.</Notice>
      )}
      {planner && planner.walletId && planner.walletStatus && planner.walletStatus !== 'Active' && (
        <Notice tone="warn">{`The wallet is ${planner.walletStatus.toLowerCase()}, so nothing can be ordered from it until the school office sorts it out.`}</Notice>
      )}
    </>
  );

  let body: React.ReactNode;
  if (loading) {
    body = <LoadingSpinner />;
  } else if (!planner) {
    body = null;
  } else if (days.length === 0) {
    body = dayMode ? null : <View style={styles.card}><Text style={styles.muted}>No data</Text></View>;
  } else if (wide && !dayMode) {
    body = <WeekGrid days={days} today={planner.today} {...slotProps} />;
  } else {
    const shown = shownDay ?? days[0];
    body = (
      <View style={styles.stack}>
        <View style={styles.dayTabs} accessibilityRole="tablist">
          {days.map((d, i) => {
            const on = d.date === shown.date;
            const isToday = isPlannerToday(d.date, planner.today);
            return (
              <TouchableOpacity key={d.date} onPress={() => setDayIndex(i)} testID={`canteen-day-${d.date}`}
                style={[styles.dayTab, isToday && styles.dayTabToday, on && { backgroundColor: colors.primary, borderColor: colors.primary }]}
                accessibilityRole="tab" accessibilityState={{ selected: on }}
                accessibilityLabel={`${shortDayName(d.date)}${isToday ? ', today' : ''}${dayHasActivity(d, selection, extras) ? ', meal ordered' : ''}`}>
                <Text style={[styles.dayTabText, on && styles.onText]}>{shortDayName(d.date)}</Text>
                {isToday && <Text style={[styles.todayTag, on && styles.onText]} testID="canteen-today-tag">Today</Text>}
                {dayHasActivity(d, selection, extras) && (
                  <View style={[styles.dayDot, on && styles.dayDotOn]} />
                )}
              </TouchableOpacity>
            );
          })}
        </View>
        {/* The green dot marks a day with a meal ordered - not today, which has its own tag. */}
        {days.some(d => dayHasActivity(d, selection, extras)) && (
          <View style={styles.dotKey}>
            <View style={styles.dayDot} />
            <Text style={styles.small}>Meal ordered</Text>
          </View>
        )}
        <DayView day={shown} priceLabel={planner.priceLabel} {...slotProps} />
      </View>
    );
  }

  const content = view === 'history' ? (
    <View style={styles.stack}>
      {header}
      {switcher}
      <CanteenHistory studentId={studentId} childName={childName} currency={cur} refreshKey={historyKey} />
      <View style={styles.spacer} />
    </View>
  ) : view === 'menu' ? (
    <View style={styles.stack}>
      {header}
      {switcher}
      {loadError && <Notice tone="error">{loadError}</Notice>}
      {loading ? <LoadingSpinner /> : planner && <CanteenMenu planner={planner} days={days} childName={childName} onOrderDay={orderDay} />}
      <View style={styles.spacer} />
    </View>
  ) : (
    <View style={styles.stack}>
      {header}
      {switcher}
      {tools}
      {dayNote}
      {toolResult}
      {notices}
      {walletBar}
      {body}
      {planner && (
        <Text style={styles.small}>
          {allergyNames.length > 0
            ? `${childName} has ${allergyNames.length === 1 ? 'an allergy' : 'allergies'} recorded by the school (${allergyNames.join(', ')}): dishes with ${allergyNames.length === 1 ? 'it' : 'them'} can't be chosen. `
            : ''}
          {declared.length > 0
            ? `${childName}'s family noted a food allergy at registration ("${declared.join('; ')}"): dishes that may contain it are marked, but can still be chosen until the school nurse confirms it. `
            : ''}
          {`Tap a chosen dish again to remove it. Each meal can be ordered until its cut-off; extras (drinks and snacks, up to 5 of one item a day) until the day's extras cut-off.${sameDay ? ` ${sameDay}` : ''}`}
        </Text>
      )}
      <View style={styles.spacer} />
    </View>
  );

  return (
    <View style={Platform.OS === 'web' ? undefined : styles.fill}>
      {Platform.OS === 'web' ? <View>{content}</View> : (
        <ScrollView style={styles.fill} showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} />}>{content}</ScrollView>
      )}

      <Modal visible={!!changing} transparent animationType="fade" onRequestClose={() => setChanging(null)}>
        <View style={styles.modalOverlay}>
          {changing && (() => {
            const others = changing.slot.options.filter(o => o.optionNo !== changing.ordered.optionNo);
            const picked = others.find(o => o.optionNo === changeTo) ?? null;
            const diff = picked ? changeDifference(changing.ordered.amount, picked.price ?? 0) : null;
            return (
              <View style={[styles.modal, styles.placedModal]} testID="canteen-change">
                <Text style={styles.modalTitle}>{`Change ${childName}'s ${changing.slot.name.toLowerCase()} on ${dayLabel(changing.day.date)}`}</Text>
                <Text style={styles.modalText}>{`Now: ${changing.ordered.itemName} · ${money(changing.ordered.amount, cur)}. Choose the dish instead:`}</Text>
                <View style={styles.options}>
                  {others.map(option => (
                    <OptionTile key={option.optionNo} option={option} styles={styles} colors={colors} currency={cur}
                      label={`Option ${option.optionNo}`} chosen={changeTo === option.optionNo} disabled={!isOptionChoosable(option)}
                      onPress={() => setChangeTo(option.optionNo)} testID={`canteen-change-option-${option.optionNo}`} />
                  ))}
                </View>
                {picked && diff && (
                  <Text style={styles.modalText} testID="canteen-change-difference">
                    {diff.direction === 'same'
                      ? `Same price - ${childName}'s wallet does not change.`
                      : diff.direction === 'more'
                        ? `${money(diff.amount, cur)} more from ${childName}'s wallet.`
                        : `${money(diff.amount, cur)} back to ${childName}'s wallet.`}
                  </Text>
                )}
                <View style={styles.modalActions}>
                  <TouchableOpacity style={styles.secondaryBtn} onPress={() => setChanging(null)} testID="canteen-change-keep">
                    <Text style={styles.secondaryBtnText}>Keep it</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.primaryBtn, { backgroundColor: colors.primary }, !picked && styles.dim]} onPress={changeOrder}
                    disabled={!picked} testID="canteen-change-confirm">
                    <Text style={styles.primaryBtnText}>Change dish</Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })()}
        </View>
      </Modal>

      <Modal visible={!!cancelling} transparent animationType="fade" onRequestClose={() => setCancelling(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modal} testID="canteen-cancel-confirm">
            <Text style={styles.modalTitle}>Cancel this order?</Text>
            {cancelling && (
              <Text style={styles.modalText}>
                {`${cancelling.description} (${money(cancelling.amount, cur)}). The money goes back to ${childName}'s wallet.`}
              </Text>
            )}
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.secondaryBtn} onPress={() => setCancelling(null)}>
                <Text style={styles.secondaryBtnText}>Keep it</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.primaryBtn, { backgroundColor: colors.danger }]} onPress={cancelOrder} testID="canteen-confirm-cancel">
                <Text style={styles.primaryBtnText}>Cancel order</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={!!pendingCopy} transparent animationType="fade" onRequestClose={() => setPendingCopy(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modal} testID="canteen-replace-confirm">
            <Text style={styles.modalTitle}>Replace chosen meals?</Text>
            {pendingCopy && (
              <Text style={styles.modalText}>
                {`This changes ${pendingCopy.result.replaced} meal${pendingCopy.result.replaced === 1 ? '' : 's'} you already chose for ${childName} this week. Nothing is ordered until you review and confirm the order.`}
              </Text>
            )}
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.secondaryBtn} onPress={() => setPendingCopy(null)} testID="canteen-replace-keep">
                <Text style={styles.secondaryBtnText}>Keep mine</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.primaryBtn, { backgroundColor: colors.primary }]} onPress={applyPendingCopy}
                testID="canteen-replace-apply">
                <Text style={styles.primaryBtnText}>Replace</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={!!review} transparent animationType="fade" onRequestClose={() => setReviewing(false)}>
        <View style={styles.modalOverlay}>
          {review && (
            <View style={[styles.modal, styles.placedModal]} testID="canteen-review">
              <View style={styles.placedHead}>
                <FeatherIcon name="clipboard" size={20} color={colors.primary} />
                <Text style={[styles.modalTitle, { flex: 1 }]}>{`Review ${orderWord}`}</Text>
                <TouchableOpacity onPress={() => setReviewing(false)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  accessibilityRole="button" accessibilityLabel="Back to edit" testID="canteen-review-close">
                  <FeatherIcon name="x" size={20} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
              <Text style={styles.modalText}>{`For ${childName}. Nothing is charged until you tap Confirm order.`}</Text>
              <ScrollView style={styles.placedList}>
                {review.lines.map((l, i) => (
                  <View key={l.key}>
                    {(i === 0 || review.lines[i - 1].date !== l.date) && (
                      <Text style={styles.reviewDay}>{l.day}</Text>
                    )}
                    <View style={styles.placedRow} testID={`canteen-review-${l.key}`}>
                      <View style={styles.placedInfo}>
                        <Text style={styles.small}>{l.slot}</Text>
                        <Text style={styles.placedItem}>{l.item}</Text>
                        <Text style={styles.small}>{`${l.quantity} × ${money(l.unitPrice, cur)}`}</Text>
                      </View>
                      <Text style={styles.placedAmount}>{money(l.amount, cur)}</Text>
                    </View>
                  </View>
                ))}
              </ScrollView>
              <View style={styles.placedTotals} testID="canteen-review-totals">
                <View style={styles.rowBetween}>
                  <Text style={styles.modalText}>Meals</Text>
                  <Text style={styles.placedAmount}>{review.meals}</Text>
                </View>
                <View style={styles.rowBetween}>
                  <Text style={styles.modalText}>Extras</Text>
                  <Text style={styles.placedAmount}>{review.extras}</Text>
                </View>
                <View style={[styles.rowBetween, styles.placedTotalRow]}>
                  <Text style={styles.placedTotalLabel}>Total from wallet</Text>
                  <Text style={styles.placedTotal}>{money(review.total, cur)}</Text>
                </View>
                <View style={styles.rowBetween}>
                  <Text style={styles.modalText}>Wallet now</Text>
                  <Text style={styles.placedAmount}>{money(review.walletBefore, cur)}</Text>
                </View>
                <View style={styles.rowBetween}>
                  <Text style={styles.modalText}>Wallet after</Text>
                  <Text style={styles.placedAmount}>{money(review.walletAfter, cur)}</Text>
                </View>
              </View>
              <TouchableOpacity style={[styles.primaryBtn, styles.placedDone, { backgroundColor: colors.success }]}
                onPress={placeWeek} disabled={!!block || busy} testID="canteen-review-confirm">
                <Text style={styles.primaryBtnText}>{`Confirm order for ${childName}`}</Text>
              </TouchableOpacity>
              <View style={styles.modalActions}>
                <TouchableOpacity style={styles.secondaryBtn} onPress={() => setReviewing(false)} testID="canteen-review-back">
                  <Text style={styles.secondaryBtnText}>Back to edit</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.secondaryBtn} onPress={discardOrder} testID="canteen-review-cancel">
                  <Text style={[styles.secondaryBtnText, { color: colors.danger }]}>Cancel</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </Modal>

      <Modal visible={!!placedResult} transparent animationType="fade" onRequestClose={() => setPlacedResult(null)}>
        <View style={styles.modalOverlay}>
          {placedResult && (() => {
            const summary = placedSummary(placedResult.placed ?? []);
            const totals = placedTotals(placedResult);
            const pcur = placedResult.currency || cur;
            return (
              <View style={[styles.modal, styles.placedModal]} testID="canteen-placed">
                <View style={styles.placedHead}>
                  <FeatherIcon name="check-circle" size={20} color={colors.success} />
                  <Text style={[styles.modalTitle, { flex: 1 }]}>Order placed</Text>
                  <TouchableOpacity onPress={() => setPlacedResult(null)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    accessibilityRole="button" accessibilityLabel="Close" testID="canteen-placed-close">
                    <FeatherIcon name="x" size={20} color={colors.textSecondary} />
                  </TouchableOpacity>
                </View>
                <Text style={styles.modalText}>
                  {summary.span ? `${childName}'s meals ${summary.span.startsWith('on ') ? summary.span : `for ${summary.span}`}` : placedResult.message}
                </Text>
                <ScrollView style={styles.placedList}>
                  {summary.lines.map(l => (
                    <View key={l.key} style={styles.placedRow} testID={`canteen-placed-${l.orderNumber}`}>
                      <View style={styles.placedInfo}>
                        <Text style={styles.small}>{l.when}</Text>
                        <Text style={styles.placedItem}>{l.item}</Text>
                        <Text style={styles.small}>{`Order ${l.orderNumber}`}</Text>
                      </View>
                      <Text style={styles.placedAmount}>{money(l.amount, pcur)}</Text>
                    </View>
                  ))}
                </ScrollView>
                {/* Stays in view below the scrolling list, with Done (utils/canteenPlanner placedTotals). */}
                <View style={styles.placedTotals} testID="canteen-placed-totals">
                  <View style={styles.rowBetween}>
                    <Text style={styles.modalText}>{`Items (${totals.items})`}</Text>
                    <Text style={styles.placedAmount}>{money(totals.subtotal, pcur)}</Text>
                  </View>
                  {totals.discount > 0 && (
                    <View style={styles.rowBetween}>
                      <Text style={styles.modalText}>Discount</Text>
                      <Text style={styles.placedAmount}>{`−${money(totals.discount, pcur)}`}</Text>
                    </View>
                  )}
                  <View style={[styles.rowBetween, styles.placedTotalRow]}>
                    <Text style={styles.placedTotalLabel}>Total</Text>
                    <Text style={styles.placedTotal}>{money(totals.total, pcur)}</Text>
                  </View>
                  <View style={styles.rowBetween}>
                    <Text style={styles.modalText}>Paid with</Text>
                    <Text style={styles.placedAmount}>{`${childName}'s wallet`}</Text>
                  </View>
                  <View style={styles.rowBetween}>
                    <Text style={styles.modalText}>Wallet before</Text>
                    <Text style={styles.placedAmount}>{money(totals.walletBefore, pcur)}</Text>
                  </View>
                  <View style={styles.rowBetween}>
                    <Text style={styles.modalText}>Wallet after</Text>
                    <Text style={styles.placedAmount}>{money(totals.walletAfter, pcur)}</Text>
                  </View>
                </View>
                <TouchableOpacity style={[styles.primaryBtn, styles.placedDone, { backgroundColor: colors.primary }]}
                  onPress={() => setPlacedResult(null)} testID="canteen-placed-done">
                  <Text style={styles.primaryBtnText}>Done</Text>
                </TouchableOpacity>
              </View>
            );
          })()}
        </View>
      </Modal>

      <BusyOverlay visible={busy || (refreshing && Platform.OS === 'web')} />
      <Toast visible={!!toast} message={toast?.message ?? ''} type={toast?.type ?? 'success'} onDismiss={() => setToast(null)} />
    </View>
  );
}

interface SlotProps {
  styles: Styles;
  colors: ThemeColors;
  selection: PlanSelection;
  extras: ExtrasSelection;
  currency: string;
  onChoose: (day: CanteenPlannerDay, slot: CanteenPlannerSlot, option: CanteenPlannerOption) => void;
  onStep: (day: CanteenPlannerDay, item: CanteenPlannerOption, delta: number) => void;
  onCancel: (target: CancelTarget) => void;
  onChange: (target: ChangeTarget) => void;
}

/** Wide screens: meals down, days across, like the approved design. */
function WeekGrid({ days, today, ...props }: SlotProps & { days: CanteenPlannerDay[]; today?: string }) {
  const { styles } = props;
  const rows = slotRows(days);
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={styles.grow}>
      <View style={[styles.card, styles.grid]} testID="canteen-week-grid">
        <View style={styles.gridRow}>
          <View style={styles.gridLabel} />
          {days.map(d => (
            <View key={d.date} style={[styles.gridCol, styles.dayHead, isPlannerToday(d.date, today) && styles.dayHeadToday]}>
              <Text style={styles.dayHeadTitle}>{dayLabel(d.date)}</Text>
              {isPlannerToday(d.date, today) && <Text style={styles.todayTag}>Today</Text>}
              <Text style={styles.small}>{d.isOrderable ? (menuWeekLabel(d.cycleWeek) || ' ') : d.closedReason ?? 'Closed'}</Text>
            </View>
          ))}
        </View>
        {rows.length === 0 && <Text style={styles.muted}>No data</Text>}
        {rows.map(row => (
          <View key={row.slotId} style={styles.gridRow}>
            <View style={[styles.gridLabel, styles.slotLabel]}>
              <Text style={styles.slotLabelText}>{row.name}</Text>
              {row.startTime && <Text style={styles.small}>{row.startTime.slice(0, 5)}</Text>}
            </View>
            {days.map(d => {
              const slot = d.slots.find(sl => sl.slotId === row.slotId);
              return (
                <View key={d.date} style={styles.gridCol}>
                  {slot ? <SlotBody day={d} slot={slot} {...props} /> : <Text style={styles.muted}>—</Text>}
                </View>
              );
            })}
          </View>
        ))}
        <View style={styles.gridRow} testID="canteen-extras-row">
          <View style={[styles.gridLabel, styles.slotLabel, styles.extrasLabel]}>
            <Text style={styles.slotLabelText}>Extras</Text>
            <Text style={styles.small}>drinks · à la carte</Text>
          </View>
          {days.map(d => (
            <View key={d.date} style={styles.gridCol}>
              <ExtrasBlock day={d} {...props} />
            </View>
          ))}
        </View>
      </View>
    </ScrollView>
  );
}

/** A phone: one day, its meals one under another. */
function DayView({ day, priceLabel, ...props }: SlotProps & { day: CanteenPlannerDay; priceLabel: string }) {
  const { styles } = props;
  return (
    <View style={styles.stack} testID={`canteen-day-view-${day.date}`}>
      <Text style={styles.small}>
        {[dayLabel(day.date), menuWeekLabel(day.cycleWeek), priceLabel].filter(Boolean).join(' · ')}
      </Text>
      {!day.isOrderable && day.closedReason && <Notice>{day.closedReason}</Notice>}
      {day.slots.length === 0 && <View style={styles.card}><Text style={styles.muted}>No data</Text></View>}
      {day.slots.map(slot => (
        <View key={slot.slotId} style={[styles.card, styles.gap8]}>
          <Text style={styles.slotLabelText}>{slot.name}</Text>
          <SlotBody day={day} slot={slot} {...props} />
        </View>
      ))}
      <ExtrasBlock day={day} {...props} />
    </View>
  );
}

/** One meal on one day: what is ordered already, or its options to choose from. */
function SlotBody({ day, slot, styles, colors, selection, currency, onChoose, onCancel, onChange }: SlotProps & { day: CanteenPlannerDay; slot: CanteenPlannerSlot }) {
  const ordered = slot.ordered;
  if (ordered) {
    return (
      <View style={styles.ordered} testID={`canteen-ordered-${day.date}-${slot.slotId}`}>
        <View style={styles.rowBetween}>
          <View style={styles.forRow}>
            <FeatherIcon name="check-circle" size={14} color={colors.success} />
            <Text style={styles.forText} numberOfLines={1}>{servedForLabel(day.date, slot.name)}</Text>
          </View>
          <StatusPill orderStatus={ordered.status} />
        </View>
        <Text style={styles.optionName}>{ordered.itemName}</Text>
        <Text style={[styles.price, { color: colors.success }]}>{money(ordered.amount, currency)}</Text>
        <Text style={styles.small} testID={`canteen-order-info-${ordered.canteenOrderId}`}>{orderInfoLine(ordered)}</Text>
        {ordered.canCancel && (
          <TouchableOpacity style={styles.cancelBtn}
            onPress={() => onCancel({
              canteenOrderId: ordered.canteenOrderId, amount: ordered.amount,
              description: `${ordered.itemName} for ${slot.name.toLowerCase()} on ${dayLabel(day.date)}`,
            })}
            testID={`canteen-cancel-${ordered.canteenOrderId}`} accessibilityRole="button">
            <Text style={[styles.cancelText, { color: colors.danger }]}>Cancel</Text>
          </TouchableOpacity>
        )}
        {ordered.canChange && (
          <TouchableOpacity style={[styles.cancelBtn, { borderColor: colors.primary }]} onPress={() => onChange({ day, slot, ordered })}
            testID={`canteen-change-${ordered.canteenOrderId}`} accessibilityRole="button">
            <Text style={[styles.cancelText, { color: colors.primary }]}>Change dish</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  const choosable = isSlotChoosable(day, slot);
  if (slot.options.length === 0) { return <Text style={styles.muted}>No data</Text>; }
  const chosen = selection[slotKey(day.date, slot.slotId)];
  return (
    <View style={styles.gap6}>
      {day.isOrderable && (
        <Text style={[styles.cut, { color: slot.isOpen ? colors.warning : colors.textMuted }]}>{cutOffText(slot)}</Text>
      )}
      <View style={styles.options}>
        {slot.options.map(option => (
          <OptionTile key={option.optionNo} option={option} styles={styles} colors={colors} currency={currency}
            label={slot.options.length > 1 ? `Option ${option.optionNo}` : null}
            chosen={chosen === option.optionNo && choosable}
            disabled={!choosable || !isOptionChoosable(option)}
            onPress={() => onChoose(day, slot, option)}
            testID={`canteen-option-${day.date}-${slot.slotId}-${option.optionNo}`} />
        ))}
      </View>
    </View>
  );
}

/** A day's extras: what is ordered already (Cancel while allowed), then each item with a − qty + stepper. */
function ExtrasBlock({ day, styles, colors, extras, currency, onStep, onCancel }: SlotProps & { day: CanteenPlannerDay }) {
  const items = day.extras ?? [];
  const orders = (day.extrasOrdered ?? []).filter(o => !/^(CANCELLED|REJECTED)$/i.test(o.status ?? ''));
  const open = areExtrasOpen(day);
  return (
    <View style={styles.extras} testID={`canteen-extras-${day.date}`}>
      <View style={styles.rowBetween}>
        <Text style={[styles.extrasTitle, { color: colors.primary }]}>Extras</Text>
        {(items.length > 0 || orders.length > 0) && (
          <Text style={[styles.cut, { color: open ? colors.warning : colors.textMuted }]}>{extrasCutOffText(day)}</Text>
        )}
      </View>
      {items.length === 0 && orders.length === 0 && <Text style={styles.muted}>No data</Text>}
      {orders.map(order => (
        <ExtrasOrderCard key={order.canteenOrderId} day={day} order={order} styles={styles} colors={colors}
          currency={currency} onCancel={onCancel} />
      ))}
      {items.map(item => {
        const quantity = extras[extraKey(day.date, item.itemId)] ?? 0;
        const max = extraMax(day, item);
        const already = extraOrderedQty(day, item.itemId);
        const priced = item.price !== null && item.price !== undefined;
        const allergy = isAllergyReason(item.blockedReason);
        const canAdd = quantity < max;
        const canRemove = quantity > 0;
        const reason = item.blockedReason ?? (priced ? null : 'Not priced');
        return (
          <View key={item.itemId} style={styles.extraRow} testID={`canteen-extra-${day.date}-${item.itemId}`}>
            <View style={[styles.fill, styles.gap2]}>
              <Text style={[styles.extraName, (!open || !!reason) && styles.dim]} numberOfLines={2}>
                {item.name}
                <Text style={[styles.small, { color: priced ? colors.textSecondary : colors.textMuted }]}>
                  {`  ${priced ? money(item.price, currency) : 'Not priced'}`}
                </Text>
              </Text>
              {item.blockedReason && (
                <Text style={[styles.small, { color: allergy ? colors.danger : colors.textSecondary }]}>{item.blockedReason}</Text>
              )}
              {item.warning && <Text style={[styles.small, { color: colors.warning }]}>{item.warning}</Text>}
              {already > 0 && (
                <Text style={styles.small}>
                  {`${already} ordered already${already >= 5 ? ' · the most for one day' : ''}`}
                </Text>
              )}
            </View>
            <View style={styles.stepper}>
              <TouchableOpacity onPress={() => onStep(day, item, -1)} disabled={!canRemove}
                style={[styles.stepBtn, !canRemove && styles.dim]} accessibilityRole="button"
                accessibilityLabel={`One fewer ${item.name}`} testID={`canteen-extra-minus-${day.date}-${item.itemId}`}>
                <FeatherIcon name="minus" size={13} color={colors.textPrimary} />
              </TouchableOpacity>
              <Text style={[styles.stepQty, quantity > 0 && { color: colors.primary }]}
                testID={`canteen-extra-qty-${day.date}-${item.itemId}`}>{quantity}</Text>
              <TouchableOpacity onPress={() => onStep(day, item, 1)} disabled={!canAdd}
                style={[styles.stepBtn, !canAdd && styles.dim]} accessibilityRole="button"
                accessibilityLabel={`One more ${item.name}`} accessibilityHint={!open ? extrasCutOffText(day) : reason ?? undefined}
                testID={`canteen-extra-plus-${day.date}-${item.itemId}`}>
                <FeatherIcon name="plus" size={13} color={colors.textPrimary} />
              </TouchableOpacity>
            </View>
          </View>
        );
      })}
    </View>
  );
}

function ExtrasOrderCard({ day, order, styles, colors, currency, onCancel }: {
  day: CanteenPlannerDay; order: CanteenPlannerExtrasOrder; styles: Styles; colors: ThemeColors; currency: string;
  onCancel: (target: CancelTarget) => void;
}) {
  const text = extrasOrderText(order);
  return (
    <View style={styles.ordered} testID={`canteen-extras-ordered-${order.canteenOrderId}`}>
      <View style={styles.rowBetween}>
        <View style={styles.forRow}>
          <FeatherIcon name="check-circle" size={14} color={colors.success} />
          <Text style={styles.forText} numberOfLines={1}>{servedForLabel(day.date, 'Extras')}</Text>
        </View>
        <StatusPill orderStatus={order.status} />
      </View>
      <Text style={styles.optionName}>{text || order.orderNumber}</Text>
      <Text style={[styles.price, { color: colors.success }]}>{money(order.amount, currency)}</Text>
      <Text style={styles.small} testID={`canteen-order-info-${order.canteenOrderId}`}>{orderInfoLine(order)}</Text>
      {order.canCancel && (
        <TouchableOpacity style={styles.cancelBtn}
          onPress={() => onCancel({
            canteenOrderId: order.canteenOrderId, amount: order.amount,
            description: `Extras (${text || order.orderNumber}) on ${dayLabel(day.date)}`,
          })}
          testID={`canteen-cancel-${order.canteenOrderId}`} accessibilityRole="button">
          <Text style={[styles.cancelText, { color: colors.danger }]}>Cancel</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

function OptionTile({ option, label, chosen, disabled, onPress, styles, colors, currency, testID }: {
  option: CanteenPlannerOption; label: string | null; chosen: boolean; disabled: boolean; onPress: () => void;
  styles: Styles; colors: ThemeColors; currency: string; testID: string;
}) {
  const allergy = isAllergyReason(option.blockedReason);
  const priced = option.price !== null && option.price !== undefined;
  return (
    <TouchableOpacity onPress={onPress} disabled={disabled} testID={testID}
      accessibilityRole="button" accessibilityState={{ selected: chosen, disabled }}
      accessibilityLabel={`${chosen ? 'Selected: ' : ''}${option.name}${priced ? `, ${money(option.price, currency)}` : ', not priced'}${option.blockedReason ? `. ${option.blockedReason}` : ''}`}
      style={[styles.option, chosen && styles.optionChosen,
        disabled && !chosen && styles.optionLocked]}>
      <View style={styles.tagRow}>
        {chosen && (
          <View style={[styles.tag, { backgroundColor: colors.primary }]} testID={`${testID}-selected`}>
            <FeatherIcon name="check-circle" size={10} color="#FFFFFF" />
            <Text style={[styles.tagText, { color: '#FFFFFF' }]}>Selected</Text>
          </View>
        )}
        {label && <Text style={styles.small}>{label}</Text>}
        {allergy && (
          <View style={[styles.tag, { backgroundColor: colors.dangerLight }]}>
            <FeatherIcon name="alert-triangle" size={10} color={colors.danger} />
            <Text style={[styles.tagText, { color: colors.danger }]}>Allergy</Text>
          </View>
        )}
        {(option.isVegan || option.isVeg) && (
          <View style={[styles.tag, { backgroundColor: colors.successLight }]}>
            <Text style={[styles.tagText, { color: colors.success }]}>{option.isVegan ? 'Vegan' : 'Veg'}</Text>
          </View>
        )}
        {option.changeNote && (
          <View style={[styles.tag, { backgroundColor: colors.primaryLight }]}>
            <Text style={[styles.tagText, { color: colors.primary }]}>Changed</Text>
          </View>
        )}
      </View>
      <View style={styles.rowBetween}>
        {!chosen && !disabled && <FeatherIcon name="plus" size={13} color={colors.textSecondary} />}
        {chosen && <FeatherIcon name="check" size={13} color={colors.primary} />}
        <Text style={[styles.optionName, styles.fill]} numberOfLines={3}>{option.name}</Text>
      </View>
      <Text style={[styles.price, { color: priced ? colors.primary : colors.textMuted }]}>
        {priced ? money(option.price, currency) : 'Not priced'}
      </Text>
      {option.blockedReason && (
        <Text style={[styles.small, { color: allergy ? colors.danger : colors.textSecondary }]}>{option.blockedReason}</Text>
      )}
      {option.warning && <Text style={[styles.small, { color: colors.warning }]}>{option.warning}</Text>}
      {option.changeNote && <Text style={styles.small}>{option.changeNote}</Text>}
    </TouchableOpacity>
  );
}

function ToolButton({ icon, label, onPress, disabled, styles, colors, testID }: {
  icon: string; label: string; onPress: () => void; disabled?: boolean; styles: Styles; colors: ThemeColors; testID: string;
}) {
  return (
    <TouchableOpacity onPress={onPress} disabled={disabled} testID={testID} accessibilityRole="button"
      style={[styles.toolBtn, disabled && styles.dim]}>
      <FeatherIcon name={icon} size={14} color={colors.primary} />
      <Text style={[styles.toolText, { color: colors.primary }]}>{label}</Text>
    </TouchableOpacity>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  stack: { gap: 10 },
  fill: { flex: 1 },
  grow: { flexGrow: 1 },
  gap6: { gap: 6 },
  gap8: { gap: 8 },
  gap2: { gap: 2 },
  dim: { opacity: 0.5 },
  spacer: { height: 24 },
  onText: { color: '#FFFFFF' },
  heroNote: { color: '#FFFFFF', opacity: 0.9, fontSize: 12, fontWeight: '600', marginTop: 2, marginBottom: 6 },
  dayDotOn: { backgroundColor: '#FFFFFF' },
  // The week's total takes all the spare room and the buttons only their own, until the bar is too
  // narrow for both (phones, or beside the sidebar): then the buttons wrap under it and fill the row.
  walletInfo: { flexGrow: 1000, flexShrink: 1, flexBasis: 0, minWidth: 220, gap: 4 },
  optionChosen: { borderStyle: 'solid', borderColor: colors.primary, backgroundColor: colors.primaryLight },
  muted: { fontSize: 12.5, color: colors.textSecondary },
  small: { fontSize: 11.5, color: colors.textSecondary },
  forRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexShrink: 1 },
  forText: { fontSize: 11.5, fontWeight: '700', color: colors.textSecondary, flexShrink: 1 },
  card: { backgroundColor: colors.card, borderRadius: 16, borderWidth: 1, borderColor: colors.border, padding: 12 },
  rowBetween: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 6 },
  tools: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  toolBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999,
    borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surface,
  },
  toolText: { fontSize: 12.5, fontWeight: '700' },
  walletBar: {
    flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: 14, borderRadius: 18,
    borderWidth: 1, borderColor: colors.border, backgroundColor: colors.primaryLight,
  },
  walletTitle: { fontSize: 15, fontWeight: '800', color: colors.textPrimary },
  walletActions: { flexGrow: 1, flexShrink: 1, flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  // Sized to its label (it used to be squeezed to 110px, narrower than "Review weekly order").
  walletBtn: { flexGrow: 1, flexShrink: 0, flexBasis: 'auto', minWidth: 170 },
  meter: { height: 8, borderRadius: 999, backgroundColor: colors.surface, overflow: 'hidden' },
  meterFill: { height: '100%', borderRadius: 999 },
  dayTabs: { flexDirection: 'row', gap: 6 },
  dayTab: {
    flex: 1, alignItems: 'center', paddingVertical: 9, borderRadius: 12, borderWidth: 1,
    borderColor: colors.border, backgroundColor: colors.surface, gap: 3,
  },
  dayTabText: { fontSize: 13, fontWeight: '800', color: colors.textSecondary },
  dayDot: { width: 5, height: 5, borderRadius: 3, backgroundColor: colors.success },
  dayTabToday: { borderColor: colors.primary, borderWidth: 1.5 },
  todayTag: { fontSize: 9.5, fontWeight: '800', letterSpacing: 0.4, textTransform: 'uppercase', color: colors.primary },
  dotKey: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 2 },
  grid: { gap: 8, minWidth: '100%' },
  gridRow: { flexDirection: 'row', gap: 8, alignItems: 'stretch' },
  gridLabel: { width: 104 },
  gridCol: { flex: 1, minWidth: 170 },
  dayHead: { padding: 8, borderRadius: 12, backgroundColor: colors.background, gap: 2 },
  dayHeadToday: { borderWidth: 1.5, borderColor: colors.primary },
  dayHeadTitle: { fontSize: 14, fontWeight: '800', color: colors.textPrimary },
  slotLabel: { justifyContent: 'center', padding: 10, borderRadius: 12, backgroundColor: colors.primaryLight },
  slotLabelText: { fontSize: 13.5, fontWeight: '800', color: colors.textPrimary },
  cut: { fontSize: 11, fontWeight: '700' },
  options: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  option: {
    flexGrow: 1, flexBasis: 130, minHeight: 62, gap: 3, padding: 9, borderRadius: 14,
    borderWidth: 1.5, borderStyle: 'dashed', borderColor: colors.border, backgroundColor: colors.surface,
  },
  optionLocked: { opacity: 0.55 },
  optionName: { fontSize: 13, fontWeight: '700', color: colors.textPrimary },
  price: { fontSize: 12, fontWeight: '800' },
  tagRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 4 },
  tag: { flexDirection: 'row', alignItems: 'center', gap: 3, borderRadius: 999, paddingHorizontal: 6, paddingVertical: 1 },
  tagText: { fontSize: 10, fontWeight: '800' },
  ordered: {
    gap: 3, padding: 9, borderRadius: 14, borderWidth: 1.5, borderColor: colors.success, backgroundColor: colors.successLight,
  },
  cancelBtn: {
    alignSelf: 'flex-start', marginTop: 4, paddingHorizontal: 12, paddingVertical: 5, borderRadius: 8,
    borderWidth: 1, borderColor: colors.danger,
  },
  cancelText: { fontSize: 12, fontWeight: '800' },
  extras: {
    gap: 8, padding: 10, borderRadius: 14, borderWidth: 1.5, borderStyle: 'dashed',
    borderColor: colors.primary, backgroundColor: colors.primaryLight,
  },
  extrasLabel: { borderWidth: 1.5, borderStyle: 'dashed', borderColor: colors.primary },
  extrasTitle: { fontSize: 13, fontWeight: '800' },
  extraRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  extraName: { fontSize: 12.5, fontWeight: '700', color: colors.textPrimary },
  stepper: {
    flexDirection: 'row', alignItems: 'center', gap: 4, borderRadius: 999, borderWidth: 1,
    borderColor: colors.border, backgroundColor: colors.surface, padding: 2,
  },
  stepBtn: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background },
  stepQty: { minWidth: 16, textAlign: 'center', fontSize: 13, fontWeight: '800', color: colors.textPrimary },
  modalOverlay: { flex: 1, backgroundColor: colors.overlay, alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { width: '100%', maxWidth: 420, backgroundColor: colors.card, borderRadius: 16, padding: 18, gap: 10 },
  modalTitle: { fontSize: 16, fontWeight: '800', color: colors.textPrimary },
  modalText: { fontSize: 13, lineHeight: 19, color: colors.textSecondary },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 4 },
  secondaryBtn: { borderRadius: 10, paddingHorizontal: 16, paddingVertical: 11, borderWidth: 1, borderColor: colors.border },
  secondaryBtnText: { color: colors.textPrimary, fontSize: 13.5, fontWeight: '700' },
  primaryBtn: { borderRadius: 10, paddingHorizontal: 16, paddingVertical: 11 },
  primaryBtnText: { color: '#FFFFFF', fontSize: 13.5, fontWeight: '800' },
  placedModal: { maxHeight: '85%' },
  reviewDay: { fontSize: 13, fontWeight: '800', color: colors.textPrimary, marginTop: 10, marginBottom: 2 },
  placedHead: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  placedList: { flexGrow: 0, flexShrink: 1, borderTopWidth: 1, borderTopColor: colors.border },
  placedRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: colors.border },
  placedInfo: { flex: 1, gap: 2 },
  placedItem: { fontSize: 13.5, fontWeight: '700', color: colors.textPrimary },
  placedAmount: { fontSize: 13.5, fontWeight: '700', color: colors.textPrimary },
  placedTotals: { gap: 6, paddingTop: 4 },
  placedTotalRow: { borderTopWidth: 1, borderTopColor: colors.border, paddingTop: 8, marginTop: 2 },
  placedTotalLabel: { fontSize: 15, fontWeight: '800', color: colors.textPrimary },
  placedDone: { alignSelf: 'stretch', alignItems: 'center', marginTop: 4 },
  placedTotal: { fontSize: 15, fontWeight: '800', color: colors.success },
});
